import React from "react";

import { css } from "@emotion/css";
import MenuItem from "@mui/material/MenuItem";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { alertCategoriesTranslations, AlertCategory, AlertType } from "@/models/AlertModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { SelectInput } from "@/uiKit/atoms/inputs/SelectInput.tsx";
import { Loader } from "@/uiKit/atoms/Loader.tsx";

export type AlertCreationDialogProps = {
  patientId: string;
  onClose: () => void;
  alertType: AlertType;
};

export const AlertCreationDialog: React.FC<AlertCreationDialogProps> = ({ patientId, alertType, onClose }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const createAlertConfig = Queries.alerts.useCreateAlertConfig();
  const [selectedCategory, setSelectedCategory] = React.useState<AlertCategory>(
    alertType == "other" ? "severe_hypoglycemia_declared" : "hypoglycemia",
  );

  let Items = null;
  if (alertType === "bgm") {
    Items = [
      <MenuItem key="bgm-hypoglycemia" value="hypoglycemia">
        {t(alertCategoriesTranslations["hypoglycemia"])}
      </MenuItem>,
      <MenuItem key="bgm-hyperglycenia" value="hyperglycemia">
        {t(alertCategoriesTranslations["hyperglycemia"])}
      </MenuItem>,
    ];
  } else if (alertType === "cgm") {
    Items = [
      <MenuItem key="cgm-hypoglycemia" value="hypoglycemia">
        {t(alertCategoriesTranslations["hypoglycemia"])}
      </MenuItem>,
      <MenuItem key="cgm-hyperglycemia" value="hyperglycemia">
        {t(alertCategoriesTranslations["hyperglycemia"])}
      </MenuItem>,
      <MenuItem key="cgm-tar" value="time_above_range">
        {t(alertCategoriesTranslations["time_above_range"])}
      </MenuItem>,
      <MenuItem key="cgm-tir" value="time_in_range">
        {t(alertCategoriesTranslations["time_in_range"])}
      </MenuItem>,
      <MenuItem key="cgm=tbr" value="time_below_range">
        {t(alertCategoriesTranslations["time_below_range"])}
      </MenuItem>,
    ];
  } else if (alertType === "other") {
    Items = [
      <MenuItem key="severe-hypo-declared" value="severe_hypoglycemia_declared">
        {t(alertCategoriesTranslations["severe_hypoglycemia_declared"])}
      </MenuItem>,
    ];
  }

  return (
    <>
      <CardHeader
        id="alert-creation-header"
        variant="contained"
        onExit={onClose}
        title={t("alertConfig.createTitle")}
      />
      <CardContent className={styles.container}>
        <SelectInput
          id="alert-creation-category"
          value={selectedCategory}
          onChange={e => e && setSelectedCategory(e as AlertCategory)}
        >
          {Items}
        </SelectInput>
      </CardContent>
      <CardFooter className={styles.footer}>
        <Button
          data-action="patient-settings.alert.cancel"
          id="cancel-alert-config"
          variant="outlined"
          onClick={onClose}
        >
          {t("button.cancel")}
        </Button>
        <Button
          id="create-alert-config"
          data-action="patient-settings.alert.create"
          variant="contained"
          startIcon={<Icons.plus className={styles.icon} />}
          onClick={() =>
            createAlertConfig.mutate(
              {
                patientId,
                alertConfig: {
                  category: selectedCategory,
                  type: alertType,
                },
              },
              {
                onSuccess: onClose,
              },
            )
          }
        >
          {createAlertConfig.isPending ? <Loader size="S" /> : t("alertConfig.create")}
        </Button>
      </CardFooter>
    </>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    margin: ${theme.spacing(16)};
  `,
  footer: css({
    paddingBottom: theme.spacing(8),
  }),
  icon: css({
    width: FontSizes.body,
    height: FontSizes.body,
  }),
});
